<script setup lang='ts'>
import Brand from './brand.vue'
import Series from './series.vue'
import Model from './model.vue'
import type { CarSelected } from './hooks'
import { useCarBrand, useCarModel, useCarSeries } from './hooks'
import type { BrandResponse, ModelResponse, SeriesItem, SeriesResponse } from '@/server/types'

const props = withDefaults(defineProps<{
  selectLevel?: 1 | 2 | 3 // 1: 选择品牌 2: 选择车系 3: 选择车型
  brandId?: number
  seriesId?: number
  modelId?: number
  placeholder?: string
  showFooter?: boolean
}>(), {
  selectLevel: 3,
  placeholder: '请选择品牌',
  showFooter: false,
})
const emits = defineEmits({
  change: (_data: CarSelected) => true,
})

const selectedBrand = ref<BrandResponse | null>(null)
const selectedSeries = ref<SeriesItem & Omit<SeriesResponse, 'seriesLs'> | null>(null)
const selectedModel = ref<ModelResponse | null>(null)

function selectBrand(brand: BrandResponse) {
  selectedBrand.value = brand
  selectedSeries.value = null
  selectedModel.value = null
  changeStep(1)
}
function selectSeries(series: SeriesItem & Omit<SeriesResponse, 'seriesLs'>) {
  selectedSeries.value = series
  selectedSeries.value.carSeriesEnergyCode = getEnergyCode(series.carSeriesEnergyCode)

  selectedModel.value = null
  changeStep(2)
}

function getEnergyCode(str = '') {
  if (!str)
    return '3'
  if (str === '纯电动')
    return '1'
  if (str === '汽油' || !str || str === '柴油' || str === '柴油,汽油' || str === '汽油,柴油')
    return '2'
  if (str.includes('混') || str.includes('增程'))
    return '3'
  return '3'
}
function selectModel(data: ModelResponse) {
  selectedModel.value = data
  changeStep(3)
}

const drawer = ref(false)
const currentStep = ref(0)
function clear(isManual = false) {
  selectedBrand.value = null
  selectedSeries.value = null
  selectedModel.value = null
  isManual && submit()
  changeStep(0)
}

function close() {
  drawer.value = false
}
function submit(isInit = false) {
  emits('change', {
    brand: unref(selectedBrand),
    series: unref(selectedSeries),
    model: unref(selectedModel),
    isInit,
  })
}
function changeStep(step: number) {
  if (step >= props.selectLevel) {
    close() // 选择到最后一步，关闭抽屉 -> 通过after-leave事件自动提交数据
    return
  }
  currentStep.value = step
}

const { init: brandInit } = useCarBrand()
const { init: seriesInit } = useCarSeries()
const { init: modelInit } = useCarModel()
async function initHandle() {
  if (props.brandId === selectedBrand.value?.id && props.seriesId === selectedSeries.value?.id && props.modelId === selectedModel.value?.id)
    return
  clear()
  if (!props.brandId)
    return submit(true)
  const brand = await brandInit(props.brandId)
  brand && selectBrand(brand)

  if (props.seriesId && brand) {
    const series = await seriesInit(props.seriesId, props.brandId)
    series && selectSeries(series)

    if (props.modelId && series) {
      const model = await modelInit(props.modelId, props.seriesId)
      model && selectModel(model)
    }
  }
  submit(true)
}
watch(() => [props.brandId, props.seriesId, props.modelId], initHandle, { immediate: true })
</script>

<template>
  <div>
    <div @click="drawer = true">
      <slot :brand="selectedBrand" :series="selectedSeries" :model="selectedModel">
        <div class="cursor-pointer border-1 border-slate-3 rounded-md px-sm py-1 text-3.5">
          <span :class="{ input__placeholder: !selectedBrand }">{{ selectedBrand ? selectedBrand.brandName : props.placeholder }}</span>
          <span v-if="selectedSeries"><b class="text-slate-3 px-1">/</b>{{ selectedSeries.carSeriesName }}</span>
          <span v-if="selectedModel && props.selectLevel === 3"><b class="text-slate-3 px-1">/</b>{{ selectedModel.carModelName }}</span>
        </div>
      </slot>
    </div>
    <NDrawer
      v-model:show="drawer"
      placement="left"
      width="500px"
      :trap-focus="false"
      :block-scroll="false"
      display-directive="show"
      @after-leave="submit()"
    >
      <NDrawerContent closable>
        <template #header>
          <span class="cursor-pointer text-4" title="选择品牌" @click="changeStep(0)">
            {{ selectedBrand?.brandName || '选择品牌' }}
          </span>
          <template v-if="[1, 2].includes(currentStep) || selectedBrand">
            <i i-ic:baseline-keyboard-arrow-right />
            <span class="cursor-pointer text-4" title="选择车系" @click="changeStep(1)">
              {{ selectedSeries?.carSeriesName || '选择车系' }}
            </span>
          </template>
          <template v-if="((currentStep === 2) || selectedSeries) && props.selectLevel === 3">
            <i i-ic:baseline-keyboard-arrow-right />
            <span class="cursor-pointer text-4" title="选择车型" @click="changeStep(2)">
              {{ selectedModel?.carModelName || '选择车型' }}
            </span>
          </template>
        </template>
        <template #default>
          <div class="overflow-hidden h-full">
            <Brand
              v-show="(currentStep === 0)"
              :brand-id="selectedBrand?.id"
              @select="selectBrand"
            />
            <Series
              v-show="(currentStep === 1 && selectedBrand?.id)"
              :brand-id="selectedBrand?.id"
              :series-id="selectedSeries?.id"
              @select="selectSeries"
            />
            <Model
              v-show="(currentStep === 2 && selectedSeries?.id)"
              :series-id="selectedSeries?.id"
              :model-id="selectedModel?.id"
              @select="selectModel"
            />
          </div>
        </template>
        <template v-if="showFooter" #footer>
          <div class="text-right">
            <NButton @click="() => clear(true)">
              清空
            </NButton>
            <NButton type="primary" class="px-6 ml-sm" @click="() => close()">
              确定
            </NButton>
          </div>
        </template>
      </NDrawerContent>
    </NDrawer>
  </div>
</template>

<style lang='scss' scoped>
.n-drawer-content:deep(.n-drawer-body-content-wrapper) {
  --uno: p-0 ;
}
.cursor-pointer {
  cursor: pointer;
}
.input {
  --uno: border-1 border-slate-2 rounded-md px-2 py-1.5;
  &__placeholder, &-tips{
    --uno: text-slate-4;
  }
}
</style>
